import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import type { ColDef } from 'ag-grid-community';
import {
  IconButton,
  UniversalModuleView,
} from '../../../ui-components/listing';
import { useGetDeletedUserListQuery } from './usersApi';
import type { User as AppUser, UserListRequest } from './model';

// ─── Date formatter ──────────────────────────────────────────────────────────────
const formatDate = (iso: any) => {
  if (!iso) return '—';
  try {
    const date = typeof iso === 'string' ? new Date(iso) : iso;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(iso);
  }
};

// ─── Column definitions ──────────────────────────────────────────────────────────
const columns: ColDef<AppUser>[] = [
  {
    headerName: 'NAME',
    field: 'fullName',
    sortable: true,
    minWidth: 160,
    cellClass: 'font-medium text-brand-600',
  },
  {
    headerName: 'EMAIL',
    field: 'email',
    sortable: true,
    minWidth: 220,
    valueGetter: (params) => params.data?.email ?? '—',
  },
  {
    headerName: 'DESCRIPTION',
    field: 'deletedReason',
    sortable: false,
    minWidth: 250,
    valueGetter: (params) => params.data?.deletedReason ?? '—',
  },
  {
    headerName: 'DELETED DATE',
    field: 'deletedDate',
    sortable: true,
    minWidth: 140,
    valueFormatter: (params) => formatDate(params.value),
  },
];

// ─── Main Page Component ─────────────────────────────────────────────────────────
const DeletedUsersPage: React.FC = () => {
  const [refreshToken, setRefreshToken] = React.useState(0);

  // ── Query state (for warmed cache) ──────────────────────────────────────────
  const queryParams: UserListRequest = useMemo(
    () => ({
      page: 1,
      size: 10,
      sortBy: 'deletedDate',
      sortDirection: 'desc',
      isDeleted: true,
    }),
    []
  );

  const { refetch } = useGetDeletedUserListQuery(queryParams);

  const handleRefresh = useCallback(() => {
    refetch();
    setRefreshToken((prev) => prev + 1);
  }, [refetch]);

  // ── Row renderer ─────────────────────────────────────────────────────────────
  const navigate = useNavigate();

  const renderTableRow = useCallback(
    (row: AppUser): React.ReactNode[] => [
      <button
        key="fullName"
        onClick={() => {
          navigate(`/users/deleted/${row.id}`);
        }}
        className="font-medium text-brand-600 hover:underline text-left truncate"
      >
        {row.fullName}
      </button>,
      <span
        key="email"
        className="text-sm text-neutral-600 dark:text-neutral-400 truncate"
      >
        {row.email ?? '—'}
      </span>,
      <span
        key="description"
        className="text-sm text-neutral-600 dark:text-neutral-400 truncate"
      >
        {row.deletedReason ?? '—'}
      </span>,
      <span
        key="deletedDate"
        className="text-sm text-neutral-600 dark:text-neutral-400"
      >
        {formatDate(row.deletedDate)}
      </span>,
    ],
    [navigate]
  );

  return (
    <UniversalModuleView<AppUser>
      moduleKey="deleted-users"
      title="Deleted Users"
      apiEndpoint="api/v1/user/deleted"
      listMethod="GET"
      columnsConfig={columns}
      rowKey={(row) => row.id}
      tableRowRenderer={renderTableRow}
      refreshToken={refreshToken}
      extraPayload={{ isDeleted: true }}
      hideFilterButton={true}
      extraActions={({ reset }) => (
        <IconButton
          icon={RefreshCw}
          title="Refresh"
          onClick={() => {
            handleRefresh();
            reset();
          }}
        />
      )}
      searchPlaceholder="Search deleted users..."
      note={
        <>
          <strong>Note:</strong> This is a read-only view. Deleted user accounts
          cannot be restored, edited, or have their status changed. Data is
          retained for compliance and audit purposes.
        </>
      }
      initialPageSize={10}
    />
  );
};

export default DeletedUsersPage;
